import React, { useEffect, useState, useRef } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import Login from './pages/Login';
import Register from './pages/Register';
import Landing from './pages/Landing';

import Header from "./components/Header";
import ChatInput from "./components/ChatInput";
import ChatMessage from "./components/ChatMessage";
import ChatImageEmbedding from "./components/ChatImageEmbedding";
import ChatVideoEmbedding from "./components/ChatVideoEmbedding";
import Loading from "./components/Loading";

import { useThread } from './hooks/useThread';
import { useRunStatus } from './hooks/useRunStatus';
import { postMessage, recoverCompletedReply } from "./services/api";
import ThreadList from "./components/ThreadList";
import { ThreadProvider } from "./context/ThreadContext";
import Plans from './pages/Plans';
import Legal from "./pages/Legal";
import { FiMenu } from "react-icons/fi";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  const [authorized, setAuthorized] = useState(null);

  useEffect(() => {
    if (!token) {
      setAuthorized(false);
      return;
    }

    fetch(`${API_URL}/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => res.json())
      .then(data => {
        if (
          data.subscription_status === "active" ||
          data.subscription_status === "trial" ||
          data.is_premium_forever
        ) {
          setAuthorized(true);
        } else {
          setAuthorized(false);
        }
      })
      .catch(() => setAuthorized(false));
  }, [token]);

  if (authorized === null) return null;
  return authorized ? children : <Navigate to="/plans" />;
};

const ChatApp = () => {
  const [requestState, setRequestState] = useState(undefined);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const username = localStorage.getItem("username") || "You";
  const {
    threadId,
    setThreadId,
    messages,
    appendOptimisticMessage,
    appendErrorMessage,
    clearThread,
    updateThreadList,
  } = useThread(requestState, setRequestState);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const { processing } = useRunStatus(requestState);

  // 🧠 Извличаме всички изображения от markdown (и HTML) отговори на асистента
  const assistantImageUrls = messages
  .filter((m) => m.role === "assistant" && typeof m.content === "string")
  .flatMap((m) => {
    const urls = [];
    const regex = /https?:\/\/[^\s"')]+/g;
    let match;
    while ((match = regex.exec(m.content))) {
      try {
        const parsed = new URL(match[0]);
        urls.push(decodeURIComponent(parsed.href));
      } catch {}
    }
    return urls;
  });

  // 📦 Готов списък със съобщения за визуализиране
  let messageList = messages
    .slice()
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .filter((message) => {
      if (message.role === "image_embedding") {
        try {
          const embedUrl = decodeURIComponent(new URL(message.content).href);
          return !assistantImageUrls.includes(embedUrl);
        } catch {
          return true;
        }
      }
      return message.hidden !== true;
    })
    .map((message, index) => {
      const key = message.id || `${message.role}-${index}`;
      if (message.role === "image_embedding") {
        return <ChatImageEmbedding url={message.content} key={key} />;
      } else if (message.role === "video_embedding") {
        return <ChatVideoEmbedding url={message.content} key={key} />;
      } else {
        return <ChatMessage message={message.content} role={message.role} username={username} key={key} />;
      }
    });

  const handleSend = async (message) => {
    if (!threadId) {
      console.error("Cannot send message, threadId is undefined!");
      return;
    }
    const knownMessageIds = new Set(
      messages.map((existingMessage) => existingMessage.id)
    );
    appendOptimisticMessage(message);
    setRequestState({ status: "in_progress" });
    try {
      const response = await postMessage(threadId, message);
      setRequestState(response);
      updateThreadList();
    } catch (error) {
      console.error(error);
      const shouldTryRecovery = !error.status || error.status >= 500;
      const recoveredResponse = shouldTryRecovery
        ? await recoverCompletedReply(threadId, knownMessageIds)
        : null;
      if (recoveredResponse) {
        setRequestState(recoveredResponse);
        updateThreadList();
        return;
      }
      setRequestState({ status: "failed" });
      appendErrorMessage();
    }
  };

  return (
    <div className="App h-screen flex overflow-hidden relative">
      {/* ☰ Бутон за мобилни устройства */}
      <button
        className="absolute top-7 left-4 z-50 bg-[#140D4F] text-white p-2 rounded-lg shadow-md hover:bg-[#1f1673] transition duration-200 md:hidden"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        <FiMenu size={24} />
      </button>

      {/* Sidebar с условна видимост */}
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-gray-900 border-r border-gray-600 z-40 transition-transform transform ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } md:relative md:translate-x-0 md:flex`}
      >
        <ThreadList
          onSelect={(thread) => {
            localStorage.setItem("thread_id", thread.thread_id);
            setThreadId(thread.thread_id);
            setSidebarOpen(false);
          }}
        />
      </div>

      <div className="flex flex-col flex-1 bg-white text-black">
        <Header onNewChat={clearThread} />
        <div className="flex flex-col grow overflow-y-auto overflow-x-hidden pb-10 z-0" ref={scrollRef}>
          {messageList}
        </div>
        <div className="px-4">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-end gap-3">
              {processing && (
                <div className="mb-2">
                  <Loading />
                </div>
              )}
              <div className="flex-1">
                <ChatInput onSend={handleSend} disabled={processing} useTextarea />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/plans" element={<Plans />} />
        <Route path="/legal" element={<Legal />} />
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <ThreadProvider>
                <ChatApp />
              </ThreadProvider>
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}

export default App;
